/**
 * @fileoverview Facebook/Meta Ads integration step for onboarding.
 * @module features/onboarding/components/steps/FacebookStep
 */

import { ReactElement } from 'react'
import { useTranslation } from 'react-i18next'
import { ArrowRight } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { ScrollReveal } from '@/shared/components/ui'
import { FacebookOAuthConnect } from '@/features/settings/components/FacebookOAuthConnect'

interface FacebookStepProps {
  /** Callback when Facebook is connected successfully */
  onComplete: () => void
  /** Callback to skip this step */
  onSkip: () => void
}

/**
 * Sixth step of onboarding - Facebook/Meta Ads connection (optional).
 * Shows a connect button that opens the Facebook OAuth popup directly.
 *
 * @component
 * @example
 * ```tsx
 * <FacebookStep onComplete={completeStep} onSkip={skipStep} />
 * ```
 */
export function FacebookStep({ onComplete, onSkip }: FacebookStepProps): ReactElement {
  const { t } = useTranslation('features-onboarding')

  return (
    <div className="flex flex-col items-center justify-center max-w-md mx-auto text-center">
      {/* Icon */}
      <ScrollReveal animateOnMount blur delay={0}>
        <div className="w-16 h-16 rounded-2xl bg-card border border-border/15 flex items-center justify-center mb-6">
          <svg className="w-8 h-8" viewBox="0 0 24 24" fill="none">
            <path
              d="M24 12c0-6.627-5.373-12-12-12S0 5.373 0 12c0 5.99 4.388 10.954 10.125 11.854V15.47H7.078V12h3.047V9.356c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.234 2.686.234v2.953H15.83c-1.491 0-1.956.925-1.956 1.874V12h3.328l-.532 3.469h-2.796v8.385C19.612 22.954 24 17.99 24 12z"
              fill="#1877F2"
            />
          </svg>
        </div>
      </ScrollReveal>

      {/* Heading */}
      <ScrollReveal animateOnMount blur delay={0.1}>
        <h2 className="text-3xl font-bold tracking-tight text-foreground mb-4">
          {t('facebook.title', 'Connect Facebook Ads')}
        </h2>
      </ScrollReveal>

      <ScrollReveal animateOnMount blur delay={0.15}>
        <p className="text-muted-foreground mb-8">
          {t('facebook.subtitle', 'Import your Meta advertising data')}
        </p>
      </ScrollReveal>

      {/* Actions */}
      <ScrollReveal animateOnMount blur delay={0.2} className="w-full">
        <div className="space-y-4">
          <FacebookOAuthConnect
            onConnected={onComplete}
            showSuccessToasts={false}
          />

          <div className="flex items-center gap-4">
            <div className="flex-1 h-px bg-border" />
            <span className="text-xs text-muted-foreground uppercase">
              {t('common.or', 'or')}
            </span>
            <div className="flex-1 h-px bg-border" />
          </div>

          <Button variant="ghost" onClick={onSkip} className="w-full opacity-50">
            {t('facebook.skip', 'Skip for now')}
            <ArrowRight className="h-4 w-4 ml-2" />
          </Button>
        </div>
      </ScrollReveal>
    </div>
  )
}
